export type TourPlacement = "right" | "left" | "top" | "bottom";

export interface TourStep {
  /** CSS selector for the element to highlight. Omit for a centered welcome/wrap-up step. */
  targetSelector?: string;
  /** Space around the highlighted element. Use 0 for targets flush with the viewport edge. */
  spotlightPadding?: number;
  /** Preferred placement on desktop. Mobile always docks at the bottom. */
  placement?: TourPlacement;
  title: string;
  body: string;
}

// Every target lives in the sidebar, which is on screen whatever page the
// owner is on. The tour never navigates, so whatever they were doing (an open
// profile, a half-written message, the workflow builder) is still there when
// it ends. A step whose target isn't shown for this account (e.g. Customer
// journey without automations) is skipped.
export const TOUR_STEPS: TourStep[] = [
  {
    title: "Welcome to Dibs",
    body: "A quick look around your dashboard. You can skip at any time.",
  },
  {
    targetSelector: '[data-tour="branch-selector"]',
    spotlightPadding: 0,
    placement: "right",
    title: "Your business menu",
    body: "Switch branches, or choose All outlets for the whole business. Settings are here too, where you connect WhatsApp, Instagram, payments and Google Reviews.",
  },
  {
    targetSelector: '[data-tour="sidebar-inbox"]',
    placement: "right",
    title: "Inbox",
    body: "Every customer conversation in one place. Your AI replies for you, and you can step in any time.",
  },
  {
    targetSelector: '[data-tour="sidebar-customers"]',
    placement: "right",
    title: "Customers",
    body: "Everyone who has booked or messaged you, with their history, notes and what your AI has learned about them.",
  },
  {
    targetSelector: '[data-tour="sidebar-analytics"]',
    placement: "right",
    title: "Analytics",
    body: "How your business is doing: bookings, revenue and conversations over time.",
  },
  {
    targetSelector: '[data-tour="sidebar-workflows"]',
    placement: "right",
    title: "Workflows",
    body: "Workflows that follow up for you, like reminders, win-backs and feedback requests. Turn on ready-made ones or build your own.",
  },
  {
    targetSelector: '[data-tour="sidebar-knowledge"]',
    placement: "right",
    title: "Knowledge",
    body: "What your AI knows and how it sounds. Every reply starts from here.",
  },
  {
    title: "You're all set",
    body: "You can take this tour again any time from Help at the top right.",
  },
];

/**
 * The steps to show: centered steps always, anchored steps only when their
 * target is on screen. On a phone the sidebar is tucked away, so every step
 * shows as a docked card instead (`keepAll`).
 */
export function stepsForPage(
  steps: TourStep[],
  hasTarget: (selector: string) => boolean,
  keepAll = false,
): TourStep[] {
  if (keepAll) return steps;
  return steps.filter((step) => !step.targetSelector || hasTarget(step.targetSelector));
}
